import React, { useState } from 'react';
import { useWebSocket } from '@/contexts/WebSocketContext';
import { Users } from 'lucide-react';

export function OnlineUsersIndicator({ onUserClick }) {
  const { onlineUsers, isConnected } = useWebSocket() || {};
  const [showPanel, setShowPanel] = useState(false);
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  
  // Include self in the count
  const allOnline = onlineUsers || [];
  const selfIncluded = allOnline.some(u => u.user_id === currentUser.id);
  const displayUsers = selfIncluded ? allOnline : [
    { user_id: currentUser.id, name: currentUser.name || 'You', avatar_url: currentUser.avatar_url },
    ...allOnline
  ];
  const count = displayUsers.length;

  return (
    <div className="relative">
      <button
        onClick={() => setShowPanel(!showPanel)}
        className="flex items-center gap-1.5 h-9 px-2.5 rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))] hover:border-emerald-500/30 transition-all"
        data-testid="online-users-btn"
      >
        {/* Stacked avatars (max 3) */}
        <div className="flex -space-x-2">
          {displayUsers.slice(0, 3).map((u, i) => (
            <div key={u.user_id || i} className="relative">
              {u.avatar_url ? (
                <img src={u.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover border-2 border-[hsl(var(--surface))]" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-[9px] font-bold border-2 border-[hsl(var(--surface))]">
                  {u.name?.charAt(0) || '?'}
                </div>
              )}
              <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full border-[1.5px] border-[hsl(var(--surface))]" />
            </div>
          ))}
        </div>
        <span className="text-xs font-medium text-emerald-400 tabular-nums" data-testid="online-count">{count}</span>
        {isConnected && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
      </button>

      {showPanel && (
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setShowPanel(false)} />
          <div className="absolute top-11 right-0 w-64 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl shadow-2xl overflow-hidden z-[9999]" data-testid="online-users-panel">
            <div className="p-3 border-b border-[hsl(var(--border))] flex items-center gap-2">
              <Users size={14} className="text-emerald-400" />
              <span className="text-sm font-semibold text-[hsl(var(--text-primary))]">Online Sekarang</span>
              <span className="ml-auto text-xs font-medium text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">{count}</span>
            </div>
            <div className="max-h-60 overflow-y-auto p-2 space-y-1">
              {displayUsers.map((u) => (
                <button key={u.user_id} onClick={() => { if (u.user_id !== currentUser.id && onUserClick) { onUserClick(u); setShowPanel(false); } }} className="w-full flex items-center gap-2.5 p-2 rounded-lg hover:bg-[hsl(var(--surface))] transition-colors cursor-pointer text-left" data-testid={`online-user-${u.user_id}`}>
                  <div className="relative flex-shrink-0">
                    {u.avatar_url ? (
                      <img src={u.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-xs font-bold">
                        {u.name?.charAt(0) || '?'}
                      </div>
                    )}
                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-400 rounded-full border-2 border-[hsl(var(--card))]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-[hsl(var(--text-primary))] truncate">
                      {u.name}{u.user_id === currentUser.id ? ' (Anda)' : ''}
                    </p>
                    {(u.department || u.position) && (
                      <p className="text-[10px] text-[hsl(var(--text-muted))] truncate">
                        {[u.department, u.position].filter(Boolean).join(' · ')}
                      </p>
                    )}
                  </div>
                </button>
              ))}
              {count === 0 && (
                <div className="p-4 text-center text-sm text-[hsl(var(--text-muted))]">Tiada pengguna online</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
